import { ModelRegistry } from './model-registry';
import { GlobalInjector } from './global-injector';
import { DomSanitizer } from '@angular/platform-browser';
import { Logger } from './logger';

import { environment } from 'environment';

export interface ModelClass<T> {
    ROOT_URL? : string;
    API? : Function;

    new(data?) : T;

    where(criteria : any, meta? : any): Promise<T[]>;
    find(id : any): Promise<T>;
    findBy(id : any);
    all(meta? : any): Promise<T[]>;
    expand<T>(this: { new(data?) : T }, items : any[]) : T[];
    mock<T>(this: { new(data?) : T }, items : any[]) : T[];
}

/**
 * Base class for API-backed models.
 * Subclass and declare a static field "ROOT_URL" to specify the REST collection being used.
 * Defaults to ApiService.tyt for the root endpoint. Specify "API" to choose the field on ApiService
 * that this model goes with.
 */
export class BaseModel {
    constructor(data?) {
        this.adapt(data);
    }

    static newFromArray<T>(this : { new(...args) : T }, data: any[]): T[] {
        return data.map(x => new this(x));
    }

    static ROOT_URL = null;
    static API = null;
    private _sanitized = {};

    /**
     * Bless a particular field as URL-safe, returns a DomSanitizer sanitized value.
     * @param field 
     */
    urlSafe(field, modifier = null) {
        if (!modifier)
            modifier = value => value;
        
        let key = `${field}:url`;
        if (this._sanitized[key])
            return this._sanitized[key];
        
        let blessed = BaseModel.injectAny<DomSanitizer>(DomSanitizer).bypassSecurityTrustUrl(modifier(this[field]));
        return this._sanitized[key] = blessed;
    }

    resourceUrlSafe(field, modifier = null) {
        if (!modifier)
            modifier = value => value;
        
        let key = `${field}:rurl`;
        if (this._sanitized[key])
            return this._sanitized[key];
        
        let blessed = BaseModel.injectAny<DomSanitizer>(DomSanitizer).bypassSecurityTrustResourceUrl(modifier(this[field]));
        return this._sanitized[key] = blessed;
    }

    /**
     * Bless a particular field as HTML-safe, returns a DomSanitizer sanitized value.
     * @param field 
     */
    htmlSafe(field, modifier = null) {
        if (!modifier)
            modifier = value => value;
        
        let key = `${field}:html`
        if (this._sanitized[key])
            return this._sanitized[key];
        
        let blessed = BaseModel.injectAny<DomSanitizer>(DomSanitizer).bypassSecurityTrustHtml(modifier(this[field]));
        return this._sanitized[key] = blessed;
    }

    /**
     * Bless a particular field as style-safe, returns a DomSanitizer sanitized value.
     * @param field 
     */
    styleSafe(field, modifier = null) {
        if (!modifier)
            modifier = value => value;
        
        let key = `${field}:style`
        if (this._sanitized[key])
            return this._sanitized[key];
        
        let blessed = BaseModel.injectAny<DomSanitizer>(DomSanitizer).bypassSecurityTrustStyle(modifier(this[field]));
        return this._sanitized[key] = blessed;
    }

    styleUrlSafe(field, modifier = null) {
        if (!modifier)
            modifier = value => value;
        return this.styleSafe(field, value => `url(${modifier(value)})`);
    }

    /**
     * Get the class of this model.
     */
    get class() : ModelClass<this> {
        return <any>this['constructor'];
    }

    /**
     * Get the API that hosts this model.
     */
    get api() : any {
        if (!this.class.API) {
            Logger.criticalFailure(`No API defined for model ${this.class.name}`);
            throw new Error(`No API defined for model ${this.class.name}`);
        }

        return BaseModel.inject(<any>this.class.API);
    }

    get rootEndpoint() : string {
        return (<any>BaseModel.inject(<any>this.class.API)).configuration.basePath;
    }

    /**
     * Get the URL of the current model instance
     */
    get itemUrl() {
        return `${this.rootEndpoint}${this.class.ROOT_URL}/${this.id}`;
    }

    /**
     * ID of this item.
     */
    id? : any;

    /**
     * Get the data of this model instance as a JSON string.
     */
    asJSON() {
        return JSON.stringify(this, (key, value) => {
            if (key && key[0] == '_')
                return undefined;
            
            return value;
        });
    }

    /**
     * Get the data of this model instance as a plain old object.
     */
    getData() {
        return JSON.parse(this.asJSON());
    }

    /**
     * Delete this model instance.
     */
    async delete(): Promise<void> {
        if (!this.id)
            throw "This model is not saved yet.";
        
        try {
            let result = await this.api.delete(this.id);
            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Insert this model instance at the server
     */
    async insert(): Promise<void> {
        if (this.id)
            throw "This model instance is already inserted.";

        let promise = this.api.create(this.getData()).toPromise();

        try {
            let response = await promise;
            let data = response.json();

            this.adapt(data);

            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update this model instance at the server
     */
    async update(): Promise<void> {
        if (!this.id)
            throw "This model instance has not yet been inserted.";

        let promise = this.api.update(this.id, this.getData()).toPromise();

        try {
            let response = await promise;
            let data = response.json();

            this.adapt(data);

            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Save this model instance (create it if it is not persisted yet, or update it if it is)
     */
    save(): Promise<void> {
        return this.id ? 
            this.update() 
            : this.insert();
    }

    private identifyModelName(key : string, value : any) {

        // By default, our type is the value type, whatever that is.
        // We'll upgrade it later, so `typeof value` is mostly used for 
        // special types 'null' and 'undefined'.

        let isArray = false;
        let modelType = null;
        let type : Function = null;
        let typeName : string = null;

        // If the object has a constructor, use that as the type of object 
        // instead of a string name.
        
        if (value !== null && value !== undefined && value.constructor) {
            type = value.constructor;
        }

        // Look up the Typescript metadata for this field on the model class.
        // If we find a usable type, use it.

        let designType : Function | string = Reflect.getMetadata("design:type", this, key);
        if (typeof designType === 'function')
            type = designType;
        else 
            typeName = designType;
    
        // If the class we get back is Array, use the additional 
        // arrayType metadata to get the type of the array. 

        if (type === Array) {
            isArray = true;
            type = null;

            let arrayType = Reflect.getMetadata("design:arrayType", this, key);
            if (typeof arrayType === 'function')
                type = arrayType;
            else 
                typeName = arrayType;
        }

        // If we ended up with a constructor (class), go ahead and 
        // look up the corresponding model name for it.

        if (typeof type === 'function')
            typeName = ModelRegistry.getNameForClass(type);

        // Remove the 'Api' prefix from the model name.

        if (typeName)
            typeName = typeName.replace(/^Api/, '');

        return typeName;
    }

    /**
     * Update this model instance locally with the data in the given object.
     * @param data The data to apply
     */
    adapt(data) {

        if (typeof data != 'object')
            throw new Error(`You cannot adapt() a value of type '${typeof data}'. Value must be an object.`);

        this._sanitized = {};
        for (let key in data) {

            // Skip the sanitization information on the object, if present
            // This is used with the DomSanitizer model features. CAUTION:
            // Traversing this key using adapt() can cause infinite traversal 
            // without skipping it here.

            if (key == '_sanitized')   
                continue;

            let value = data[key];
            let modelType = this.identifyModelName(key, value);

            // If we have the name of a model type, go ahead and inflate the value 
            // to be an instance of it, using ModelRegistry.inflate().

            if (modelType) {
                if (modelType == 'Array') {
                    Logger.failure(`While adapting data: Could not identify array type`);
                    debugger;
                }

                value = ModelRegistry.inflate(modelType, value);
            }

            // Assign the resulting value into our own object.
            // At this point, `value` is either the raw value from the JSON 
            // entity data, or is an instance of a model class.

            try {
                this[key] = value;
            } catch (e) {
                Logger.error(`Failed to set key ${key}: ${e.message}`, e);
            }
        }
    }

    static getApi(cls : any) : any {
        if (!this.API) {
            Logger.criticalFailure(`No API defined for model ${this.name}`);
            throw new Error(`No API defined for model ${this.name}`);
        }

        let api = this.inject(<any>this.getClass(this).API);
        if (!api) 
            throw new Error(`Failed to inject API for model ${this.name} (${this.API.name}), injection produced null/undefined`);

        return api;
    }

    static getRootUrl(cls : any) : string {
        return BaseModel.getClass(this).ROOT_URL;
    }

    static getClass<T>(cls: { new(data?) : T }) : ModelClass<T> {
        return <ModelClass<T>>cls;
    }

    /**
     * Find a specific model instance by ID.
     * @param this The model class 
     * @param id The ID to search for 
     */
    static find<T>(this: { new(data?) : T }, id : any): Promise<T> {
        let api = (<any>this).getApi(this);
        
        if (!api) 
            throw new Error(`${api.constructor.name} does not have an API`);
            
        if (!api.get)
            throw new Error(`${api.constructor.name} does not provide get()`);

        return api.get(id).toPromise();
    }

    static prepareCriteria(criteria : any) {
        if (!criteria)
            return {};

        // clone the criteria

        criteria = JSON.parse(JSON.stringify(criteria));
        
        // locate any references to model objects and replace them with their corresponding IDs if possible
        // also delete any keys with undefined as a value

        for (let key in criteria) {
            let value = criteria[key];

            if (value === undefined)
                delete criteria[key];
            else if (value instanceof BaseModel && value.id) {
                delete criteria[key];
                if (key.endsWith('_id'))
                    criteria[key] = value.id;
                else
                    criteria[`${key}_id`] = value.id;
            }
        }

        return criteria;
    }

    /**
     * Quickly create model instances for all data items in an array.
     * @param this The model class 
     * @param items An array of data items to use for instantiating the objects.
     */
    static expand<T>(this: { new(data?) : T }, items : any[]) : T[] {
        return items.map(x => new this(x));
    }

    /**
     * Convenient way to mock a set of proper model objects from a set of raw data.
     * @param this The model class 
     * @param items An array of data items to use for instantiating the mock objects.
     */
    static mock<T>(this: { new(data?) : T }, items : any[]) : T[] {
        return BaseModel.getClass(this).expand(items);
    }

    static inject<T>(token : { new(...args) : T }) : T {
        if (token === null || token === undefined) {
            Logger.criticalFailure(`Attempt was made to inject a null/undefined token!`);
            throw new Error(`Attempt was made to inject a null/undefined token!`);
        }
        try {
            let injector = GlobalInjector.injector;
            return injector.get(token);
        } catch (e) {
            if (e.message.match(/No provider for/)) {
                // You're fucking welcome --Liam

                if (environment.testing) {
                    throw new Error(`No provider for token ${token.name}. You need to mock this class in your test.`);
                } else {
                    throw new Error(
                        `No provider for token ${token.name}. This error can occur if APIs are used in `
                        + `ngOnInit within direct subcomponents of AppComponent before the application `
                        + `has finished initializing. Try moving your usage of the model/API to ngAfterViewInit`
                    );
                }
            } else {
                Logger.criticalFailure(`Could not inject token ${token.name}`);
                Logger.error(`Error while injecting token`, e);
            }
        }
    }

    static injectAny<T>(token : any) : T {
        return GlobalInjector.injector.get(token);
    }

    /**
     * Fetch all instances of this model class.
     * @param this 
     */
    static all<T>(this: { new(data?) : T }, count? : number, offset? : number): Promise<T[]> {
        let api = (<any>this).getApi(this)

        if (!api.all)
            throw new Error(`${api.constructor.name} does not provide all()`);

        return api
            .all(JSON.stringify(BaseModel.prepareCriteria({ count: count, offset: offset })))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ; 
    }

    /**
     * Find a specific model instance by arbitrary criteria
     * @param this The model class 
     * @param id The ID to search for 
     */
    static async findBy<T>(this: { new(data?) : T }, criteria : any): Promise<T> {
        criteria = BaseModel.prepareCriteria(criteria);
        criteria.limit = 1;
        let api = (<any>this).getApi(this);

        if (!api.all)
            throw new Error(`${api.constructor.name} does not provide all()`);

        let results = await api
            .all(JSON.stringify(criteria))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ;

        return results[0];
    }

    /**
     * Fetch all matching model instances based on the given criteria
     * @param this The model class
     * @param criteria The criteria to search for
     */
    static where<T>(this: { new(data?) : T }, criteria : any): Promise<T[]> {
        criteria = BaseModel.prepareCriteria(criteria);
        let api = (<any>this).getApi(this);

        if (!api.all)
        throw new Error(`${api.constructor.name} does not provide all()`);

        return api
            .all(JSON.stringify(criteria))
            .map(x => x.json ? x.json() : x)
            .toPromise()
            .catch(response => { throw response.json ? response.json() : response; })
        ;
    } 
}

